import { OperatingHours as OperatingHoursModel } from '@leverege/operating-hours'
import { Text } from '@leverege/ui-elements'
import { DateTime } from 'luxon'
import React, { useCallback, useMemo, useRef, useState } from 'react'

const HOURS_STRING_FORMAT = 'hh:mma'

export default function OperatingHours( props ) {
  const { projection, operatingHours, operatingHoursMargin, numRows } = props

  const ranges = useMemo( () => {
    const timezone = OperatingHoursModel.getTimezone( operatingHours )
    let currentDate = DateTime.fromMillis( projection.startValue, { zone : timezone } ).startOf( 'day' )
    const stopDate = DateTime.fromMillis( projection.stopValue, { zone : timezone } )

    if ( currentDate < stopDate ) {
      const elems = []
      do {
        const todayHours = OperatingHoursModel.getHoursForDate( operatingHours, currentDate.toJSDate() )
        if ( todayHours != null && todayHours.open && todayHours.ranges?.length > 0 ) {
          for ( let i = 0; i < todayHours.ranges.length; i++ ) {
            const range = todayHours.ranges[i]
            const start = range.start
            const end = range.end
            const startDate = DateTime.fromFormat(
              start,
              HOURS_STRING_FORMAT,
              { zone : timezone }
            )
              .set( {
                day : currentDate.day,
                month : currentDate.month,
                year : currentDate.year
              } )
            const endDate = DateTime.fromFormat(
              end,
              HOURS_STRING_FORMAT,
              { zone : timezone }
            )
              .set( {
                day : currentDate.day,
                month : currentDate.month,
                year : currentDate.year
              } )

            elems.push(
              <Range
                key={startDate.toLocaleString()}
                keyBase={todayHours.name}
                start={start}
                end={end}
                startDate={startDate}
                endDate={endDate}
                height={numRows * 40}
                projection={projection}
                operatingHoursMargin={operatingHoursMargin} />
            )
          }
        }
        currentDate = currentDate.plus( { day : 1 } )
      } while ( currentDate < stopDate )

      return elems
    }

    return null
  }, [ projection.startValue, projection.stopValue, operatingHours, operatingHoursMargin, numRows ] )

  return ranges
}

function Range( {
  keyBase,
  start,
  end,
  projection,
  startDate,
  endDate,
  height,
  operatingHoursMargin
} ) {
  const [ rangeRollover, setRangeRollover ] = useState( false )
  const rolloverTimer = useRef()

  const onRangeOver = useCallback( () => {
    rolloverTimer.current = setTimeout( () => {
      setRangeRollover( true )
    }, 500 )
  }, [] )

  const onRangeOut = useCallback( () => {
    clearTimeout( rolloverTimer.current )
    setRangeRollover( false )
  }, [] )
  
  const ohLeft = projection.startToScreen( startDate.toMillis() )
  const ohRight = projection.stopToScreen( endDate.toMillis() )
  const ohWidth = ohRight - ohLeft
  const sbLeft = projection.startToScreen( startDate.minus( { seconds : operatingHoursMargin } ) )
  const sbRight = ohLeft
  const sbWidth = sbRight - sbLeft
  const ebLeft = ohRight
  const ebRight = projection.stopToScreen( endDate.plus( { seconds : operatingHoursMargin } ).toMillis() )
  const ebWidth = ebRight - ebLeft
  return [
    <div
      onMouseEnter={onRangeOver}
      onMouseLeave={onRangeOut}
      key={`${keyBase}-${start}-${end}-sb`}
      style={{
        height,
        width : sbWidth,
        backgroundColor : 'var(--color-operatingHoursRangeBuffer, rgba(0,0,0,0.05))',
        left : sbLeft,
        top : 0,
        position : 'absolute',
        zIndex : 1
      }} />,
    <Text
      variant="captionCenter|caption"
      style={{
        width : sbWidth,
        left : sbLeft,
        top : height,
        position : 'absolute',
        zIndex : 1,
        textAlign : 'center',
        opacity : rangeRollover ? 1 : 0,
        transition : 'all 0.2s'
      }}
      key={`${keyBase}-sb-label`}>
      Buffer
    </Text>,
    <div
      onMouseEnter={onRangeOver}
      onMouseLeave={onRangeOut}
      key={`${keyBase}-${start}-${end}`}
      style={{
        height,
        width : ohWidth,
        backgroundColor : 'var(--color-operatingHoursRange, rgba(0,255,0,0.05))',
        left : ohLeft,
        top : 0,
        position : 'absolute',
        zIndex : 1,
      }} />,
    <Text
      variant="captionCenter|caption"
      style={{
        width : ohWidth,
        left : ohLeft,
        top : height,
        position : 'absolute',
        zIndex : 1,
        textAlign : 'center',
        opacity : rangeRollover ? 1 : 0,
        transition : 'all 0.2s'
      }}
      key={`${keyBase}-label`}>
      Business Hours
    </Text>,
    <div
      onMouseEnter={onRangeOver}
      onMouseLeave={onRangeOut}
      key={`${keyBase}-${start}-${end}-eb`}
      style={{
        height,
        width : ebWidth,
        backgroundColor : 'var(--color-operatingHoursRangeBuffer, rgba(0,0,0,0.05))',
        left : ebLeft,
        top : 0,
        position : 'absolute',
        zIndex : 1
      }} />,
    <Text
      variant="captionCenter|caption"
      style={{
        width : ebWidth,
        left : ebLeft,
        top : height,
        position : 'absolute',
        zIndex : 1,
        textAlign : 'center',
        opacity : rangeRollover ? 1 : 0,
        transition : 'all 0.2s'
      }}
      key={`${keyBase}-eb-label`}>
      Buffer
    </Text>
  ]
}
